import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Search, X, ArrowRight, ChevronRight } from 'lucide-react';

/**
 * CommandPalette — IDE-style ⌘K fuzzy search modal for fast access to actions,
 * navigation, and dot-phrase insertion. Triggered by Cmd+K (Mac) or Ctrl+K
 * (Windows/Linux) anywhere in the app. Keyboard-first: arrow keys to navigate,
 * Enter to execute, Esc to dismiss.
 *
 * Action shape:
 *   { id, title, subtitle?, group, shortcut?, run: () => void, keywords?: [...] }
 */
export const CommandPalette = ({ open, onClose, actions = [] }) => {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Naive fuzzy filter — substring + initial-letter match. Fast enough at <500 actions.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return actions;
    return actions
      .map((a) => {
        const hay = [a.title, a.subtitle, a.group, ...(a.keywords || [])]
          .filter(Boolean).join(' ').toLowerCase();
        let score = 0;
        if (hay.includes(q)) score += 5;
        if (a.title?.toLowerCase().startsWith(q)) score += 8;
        // Initials match: "ssg" matches "Sticky Schedule Generator"
        const initials = a.title?.toLowerCase().split(/\s+/).map(w => w[0]).join('') || '';
        if (initials.startsWith(q)) score += 6;
        return { a, score };
      })
      .filter(x => x.score > 0)
      .sort((x, y) => y.score - x.score)
      .map(x => x.a);
  }, [query, actions]);

  // Group for visual rendering
  const grouped = useMemo(() => {
    const groups = {};
    filtered.forEach((a) => {
      const g = a.group || 'Other';
      if (!groups[g]) groups[g] = [];
      groups[g].push(a);
    });
    return groups;
  }, [filtered]);

  // Flat list mirroring visual order — for keyboard navigation
  const flatList = useMemo(() => {
    return Object.values(grouped).flat();
  }, [grouped]);

  useEffect(() => {
    if (activeIndex >= flatList.length) setActiveIndex(0);
  }, [flatList.length, activeIndex]);

  // Keyboard handlers
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
      else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, flatList.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const action = flatList[activeIndex];
        if (action) {
          action.run?.();
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, flatList, activeIndex, onClose]);

  // Scroll active row into view
  useEffect(() => {
    if (!open || !listRef.current) return;
    const active = listRef.current.querySelector('[data-active="true"]');
    if (active) active.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open]);

  if (!open) return null;

  let runningIdx = -1;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh] bg-black/60 backdrop-blur-sm px-4"
      onClick={onClose}
      data-testid="command-palette-backdrop"
    >
      <div
        className="bg-card border border-amber-500/30 rounded-lg shadow-2xl w-full max-w-2xl flex flex-col max-h-[70vh]"
        onClick={(e) => e.stopPropagation()}
        data-testid="command-palette"
      >
        {/* Header / Search */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
          <Search className="w-4 h-4 text-amber-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Type a command, shortcut, or dot-phrase…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActiveIndex(0); }}
            className="flex-1 bg-transparent outline-none text-sm text-foreground placeholder:text-muted-foreground"
            data-testid="command-palette-input"
          />
          <kbd className="hidden sm:inline-flex items-center gap-0.5 text-[10px] text-muted-foreground border border-border rounded px-1.5 py-0.5 font-mono">
            ESC
          </kbd>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
            data-testid="command-palette-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Results */}
        <div ref={listRef} className="flex-1 overflow-y-auto py-1">
          {flatList.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              No commands match "<span className="font-mono">{query}</span>".
            </div>
          ) : (
            Object.entries(grouped).map(([group, items]) => (
              <div key={group} className="py-1">
                <div className="px-4 py-1 text-[10px] uppercase tracking-wider text-muted-foreground/70 font-semibold">
                  {group}
                </div>
                {items.map((a) => {
                  runningIdx += 1;
                  const isActive = runningIdx === activeIndex;
                  return (
                    <button
                      key={a.id}
                      type="button"
                      data-active={isActive}
                      onMouseEnter={() => setActiveIndex(runningIdx)}
                      onClick={() => { a.run?.(); onClose(); }}
                      className={`w-full flex items-center gap-3 px-4 py-2 text-left transition-colors ${
                        isActive ? 'bg-amber-500/15 text-amber-200' : 'hover:bg-secondary/40 text-foreground'
                      }`}
                      data-testid={`command-palette-action-${a.id}`}
                    >
                      <div className={`flex-shrink-0 w-6 h-6 rounded flex items-center justify-center ${
                        isActive ? 'bg-amber-500/30 text-amber-300' : 'bg-secondary/40 text-muted-foreground'
                      }`}>
                        {a.icon ? <a.icon className="w-3.5 h-3.5" /> : <ChevronRight className="w-3 h-3" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{a.title}</div>
                        {a.subtitle && (
                          <div className="text-[11px] text-muted-foreground truncate">{a.subtitle}</div>
                        )}
                      </div>
                      {a.shortcut && (
                        <kbd className="text-[10px] text-muted-foreground border border-border rounded px-1.5 py-0.5 font-mono whitespace-nowrap">
                          {a.shortcut}
                        </kbd>
                      )}
                      {isActive && <ArrowRight className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer hint */}
        <div className="border-t border-border px-4 py-2 flex items-center justify-between text-[10px] text-muted-foreground">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <kbd className="border border-border rounded px-1 font-mono">↑</kbd>
              <kbd className="border border-border rounded px-1 font-mono">↓</kbd>
              navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="border border-border rounded px-1 font-mono">↵</kbd>
              run
            </span>
            <span className="flex items-center gap-1">
              <kbd className="border border-border rounded px-1 font-mono">esc</kbd>
              close
            </span>
          </div>
          <span>{flatList.length} command{flatList.length === 1 ? '' : 's'}</span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
